import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/** Types */
import {
  PreviewQuestionOption,
  PreviewQuestion,
  PreviewAnswerSet,
  PreviewMode,
} from "./PreviewType";

/** Entities */
import { CX_SCORE_VAL, CxQuestionType } from "../../entity/entities";

/** Util */
import { AssertUtil } from "../../util/assert-util";

/** Constants */
import { ZIPCODE_REGEX } from "../../Constants";

const {
  FREE_TEXT,
  CHECKBOX,
  RADIO_BUTTON,
  ZIPCODE,
  SELECT_SCORE,
  DROPDOWN,
  CALENDAR,
} = CxQuestionType;

/** Set initial state for preview mode */
const initialState: PreviewMode = {
  isReviewMode: false,
  errorCount: 0,
  questionPages: [],
  answer: [],
  coupon: {
    isCoupon: false,
    imageUrl: "",
    topMessage: "",
    caution: "",
    isBase64: false,
  },
};

/** Extra function */
const selectCxAnswer = (
  state: PreviewMode,
  indexes: {
    pageIndex: number;
    questionIndex: number;
  }
) => {
  const { pageIndex, questionIndex } = indexes;
  const cxAnswerPage = state.answer[pageIndex];
  const cxAnswer = cxAnswerPage.cxAnswers[questionIndex];

  return cxAnswer;
};

export const selectCxAnswerSet = (
  cxAnswerSets: PreviewAnswerSet[],
  cxQuestionOption: PreviewQuestionOption
) => {
  for (const cxAnswerSet of cxAnswerSets)
    if (cxAnswerSet.id === cxQuestionOption.id) return cxAnswerSet;

  throw new Error(`cxAnswerSet id=${cxQuestionOption.id} not found`);
};

/** Init answer */
const initAnswerByPages = (cxQuestionPages: any) =>
  cxQuestionPages.map((cxQuestionPage: any) => {
    return {
      cxQuestionPageId: cxQuestionPage.id,
      answered: false,
      cxAnswers: cxQuestionPage.questions.map((cxQuestion: any) => {
        let answerStr: string | null;
        switch (cxQuestion.questionType) {
          case FREE_TEXT:
          case ZIPCODE:
          case CALENDAR:
            answerStr = "";
            break;
          case CHECKBOX:
          case DROPDOWN:
          case RADIO_BUTTON:
          case SELECT_SCORE:
            answerStr = null;
            break;
          default:
            AssertUtil.unreachable(cxQuestion.questionType as never);
        }

        const cxAnswerSets: PreviewAnswerSet[] = [];

        if (cxQuestion.questionOptions) {
          for (const cxQuestionOption of cxQuestion.questionOptions) {
            const cxAnswerSet: PreviewAnswerSet = {
              id: cxQuestionOption.id!,
              answerStr: "",
              answerSubStr: "",
            };
            cxAnswerSets.push(cxAnswerSet);
          }
        }

        return {
          cxQuestionId: cxQuestion.id!,
          questionType: cxQuestion.questionType,
          answerStr,
          answerSubStr: "",
          cxAnswerSets,
          answerScore: null,
        };
      }),
    };
  });

/** Helper function */
const selectQuestion = (args: {
  state: PreviewMode;
  pageIndex: number;
  questionIndex: number;
}): any => {
  const { state, pageIndex, questionIndex } = args;
  const cxQuestionPage = state.questionPages[pageIndex];
  const cxQuestion = cxQuestionPage.questions[questionIndex];

  return cxQuestion;
};

const selectCxQuestionOptionByStr = (
  cxQuestion: PreviewQuestion,
  answerStr: string
) => {
  for (const cxQuestionOption of cxQuestion.questionOptions)
    if (cxQuestionOption.optionText === answerStr) return cxQuestionOption;

  return null;
};

/** Check page answered */
const answered = (args: {
  state: PreviewMode;
  pageIndex: number;
  questionIndex: number;
}): boolean => {
  const { state, pageIndex, questionIndex } = args;
  const cxAnswer = selectCxAnswer(state, {
    pageIndex,
    questionIndex,
  });

  const cxQuestion = selectQuestion({
    state,
    pageIndex,
    questionIndex,
  });

  if (cxAnswer.questionType === SELECT_SCORE) {
    if (!cxQuestion.required) return true;
    if (cxAnswer.answerScore === null) return false;
  } else if (cxAnswer.questionType === RADIO_BUTTON) {
    const notSelected =
      cxAnswer.answerStr === null || cxAnswer.answerStr.trim() === "";

    if (notSelected) {
      if (cxQuestion.required) return false;
      return true;
    }

    const cxQuestionOption = selectCxQuestionOptionByStr(
      cxQuestion,
      cxAnswer.answerStr!
    );

    if (!cxQuestionOption) return true;
    if (!cxQuestionOption.withFreeText) return true;
    if (!cxQuestionOption.freeTextRequired) return true;
    if (cxAnswer.answerSubStr) return true;

    return false;
  } else if (
    cxAnswer.questionType === DROPDOWN ||
    cxAnswer.questionType === FREE_TEXT ||
    cxAnswer.questionType === CALENDAR ||
    false
  ) {
    if (!cxQuestion.required) return true;
    if (cxAnswer.answerStr === null || cxAnswer.answerStr.trim() === "")
      return false;
  } else if (cxAnswer.questionType === CHECKBOX) {
    if (cxQuestion.required) {
      let checkedCnt = 0;

      for (const cxAnswerSet of cxAnswer.cxAnswerSets)
        if (cxAnswerSet.answerStr) checkedCnt += 1;

      if (checkedCnt <= 0) return false;
    }

    let answered = true;
    for (const cxAnswerSet of cxAnswer.cxAnswerSets) {
      const cxQuestionOption = selectCxQuestionOptionByStr(
        cxQuestion,
        cxAnswerSet.answerStr
      );

      if (cxQuestionOption) {
        if (cxQuestionOption.withFreeText && cxQuestionOption.freeTextRequired)
          if (!cxAnswerSet.answerSubStr) answered = false;
      }
    }

    return answered;
  } else if (cxAnswer.questionType === ZIPCODE) {
    if (!cxAnswer.answerStr) {
      if (cxQuestion.required) return false;
      return true;
    }

    if (ZIPCODE_REGEX.test(cxAnswer.answerStr)) return true;

    return false;
  } else {
    AssertUtil.unreachable(cxAnswer.questionType);
  }

  return true;
};
export const pageAnswered = (args: {
  state: PreviewMode;
  pageIndex: number;
}): boolean => {
  const { state, pageIndex } = args;
  if (typeof pageIndex !== "number" || pageIndex < 0) return false;

  const cxAnswerPage = state.answer[pageIndex];
  return !cxAnswerPage.cxAnswers.some(
    (_, answerIndex) =>
      !answered({
        state,
        pageIndex,
        questionIndex: answerIndex,
      })
  );
};

/**
 * Check questionData(without save) will have not any question options id,
 * we must check and assign a dummy id for the question options
 */
const dummyQuestionDataCheckBox = (questionData: []) =>
  questionData.map(
    (item: { questions: [] }) =>
      item && {
        ...item,
        questions: item.questions.map((question: PreviewQuestion) =>
          question.questionType === CHECKBOX
            ? {
                ...question,
                questionOptions: question.questionOptions.map(
                  (questionOption: PreviewQuestionOption) => ({
                    ...questionOption,
                    id: questionOption.id ?? questionOption.dispOrder,
                  })
                ),
              }
            : question
        ),
      }
  );

/** Reducer */
export const previewSlice = createSlice({
  name: "preview",
  initialState,
  reducers: {
    cxSurveyPreviewModeHomeq: (
      state,
      action: PayloadAction<{ message: any }>
    ) => {
      const { questionData, questionPage, cxCoupon } = action.payload.message;
      const tempQuestionData = dummyQuestionDataCheckBox(questionData);
      const questionPagePreview = questionPage.map(
        (item: { id: number; title: string }, index: number) => {
          return {
            ...item,
            ...tempQuestionData[index],
          };
        }
      );

      /** Remove the first element from questionPage, reset index to 0 */
      questionPagePreview.shift();

      state.isReviewMode = true;
      state.questionPages = questionPagePreview;
      const { imageUrl, caution, topMessage, isBase64 } = cxCoupon || {};
      state.coupon = {
        isCoupon: !!imageUrl,
        imageUrl,
        caution,
        topMessage,
        isBase64,
      };

      /** Handle set init answer for current page */
      state.answer = initAnswerByPages(questionPagePreview);
    },
    resetPreviewToInitialState: (state) => {
      state = initialState;
    },
    resetScore: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
      }>
    ) => {
      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerScore = null;
    },
    selectScore: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
        score: CX_SCORE_VAL;
      }>
    ) => {
      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerScore = action.payload.score;
    },
    changeFreeText: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
        str: string;
      }>
    ) => {
      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerStr = action.payload.str;
    },
    changeCalendar: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
        str: string;
      }>
    ) => {
      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerStr = action.payload.str;
    },
    changeCheckBox: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
        cxQuestionOption: PreviewQuestionOption;
        value: string;
        checked: boolean;
      }>
    ) => {
      const { checked, cxQuestionOption } = action.payload;

      const cxAnswer = selectCxAnswer(state, action.payload);
      const cxAnswerSet = selectCxAnswerSet(
        cxAnswer.cxAnswerSets,
        cxQuestionOption
      )!;

      if (!checked) {
        cxAnswerSet.answerStr = "";
        cxAnswerSet.answerSubStr = "";
      } else {
        if (cxAnswerSet.answerStr !== cxQuestionOption.optionText) {
          cxAnswerSet.answerStr = cxQuestionOption.optionText;
          cxAnswerSet.answerSubStr = "";
        }
      }
    },
    changeCheckBoxFreeText: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
        cxQuestionOption: PreviewQuestionOption;
        freeText: string;
      }>
    ) => {
      const { cxQuestionOption, freeText } = action.payload;

      const cxAnswer = selectCxAnswer(state, action.payload);
      const cxAnswerSet = selectCxAnswerSet(
        cxAnswer.cxAnswerSets,
        cxQuestionOption
      )!;

      cxAnswerSet.answerSubStr = freeText;
    },
    changeRadioButton: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
        value: string;
      }>
    ) => {
      const cxAnswer = selectCxAnswer(state, action.payload);

      if (cxAnswer.answerStr !== action.payload.value) {
        cxAnswer.answerStr = action.payload.value;
        cxAnswer.answerSubStr = "";
      }
    },
    changeRadioButtonFreeText: (
      state,
      action: PayloadAction<{
        pageIndex: number;
        questionIndex: number;
        freeText: string;
      }>
    ) => {
      const cxAnswer = selectCxAnswer(state, action.payload);
      cxAnswer.answerSubStr = action.payload.freeText;
    },
    answeredToPage: (
      state,
      action: PayloadAction<{
        pageIndex: number;
      }>
    ) => {
      const pageIndex = action.payload.pageIndex;
      state.answer[pageIndex].answered = true;
    },
    changeErrorCount: (
      state,
      action: PayloadAction<{
        errorCount: number;
      }>
    ) => {
      state.errorCount = action.payload.errorCount;
    },
  },
});

export const {
  cxSurveyPreviewModeHomeq,
  selectScore,
  resetScore,
  changeFreeText,
  changeCalendar,
  changeCheckBoxFreeText,
  changeCheckBox,
  changeRadioButtonFreeText,
  changeRadioButton,
  answeredToPage,
  resetPreviewToInitialState,
  changeErrorCount,
} = previewSlice.actions;

export const previewReducer = previewSlice.reducer;
